<template>
  <div
    class="accent-cards"
    :style="cssStyles"
    :class="accentCardsClass"
  >
    <div
      class="accent-cards__wrapper accent-cards__wrapper--client"
      :class="wrappClass"
    >
      <div
        v-for="article in articles"
        :key="article.id"
        class="accent-cards__card-wrapper"
      >
        <AccentCard
          class="accent-cards__card"
          :article="article"
          :size="articles.length === 1 && area !== 'sidebar' ? 'big' : 'small'"
          @click-article-link="$emit('onClickItem', article)"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import AccentCard from '~/components/AccentCard.vue';
  import type { TArticle } from '~/types/common';

  export default defineNuxtComponent({
    name: 'AccentCardsWrapp',

    components: {
      AccentCard,
    },

    props: {
      articles: {
        type: Array as PropType<TArticle[]>,
        required: true,
      },

      area: {
        type: String as PropType<string | undefined>,
        required: true,
      },
    },

    computed: {
      accentCardsClass(): Record<string, any> {
        const areas: Array<string> = [
          'above-comments',
          'below-comments',
          'home',
          'sidebar',
          'after-first-paragraph',
          'post-dynamic',
        ];
        return {
          [`accent-cards--${this.area}`]: areas.includes(this.area ? this.area : ''),
        };
      },

      wrappClass(): Record<string, boolean> {
        return {
          [`accent-cards__wrapper--${this.articles.length}`]: this.articles.length > 0,
        };
      },

      cssStyles(): Record<string, string> {
        return {
          '--accent-card-padding': this.area === 'sidebar' ? '24px 16px' : '0px 16px',
        };
      },
    },
  });
</script>

<style lang="scss" scoped>
  .accent-cards {
    position: relative;
    &__wrapper {
      display: grid;
      grid-template-columns: 100%;
      grid-row-gap: $spacing-3;
      padding: var(--accent-card-padding, $spacing-6 $spacing-4);
      background: black;
      @include big-tablet {
        padding: var(--accent-card-padding, $spacing-10);
      }
      &--1 {
        :deep() {
          .accent-card--big {
            .accent-card__image-wrapp {
              max-height: 288px;
              @include tablet {
                max-height: 378px;
              }
            }
          }
        }
      }
      &--2,
      &--4 {
        :deep() {
          .accent-card--small {
            min-height: 400px;
            max-height: 400px;
            @include tablet {
              min-height: 400px;
              max-height: 440px;
            }
            .accent-card__title {
              font-size: 20px;
              line-height: 30px;
              @include tablet {
                font-size: 22px;
                line-height: 32px;
              }
              @include big-tablet {
                font-size: 26px;
                line-height: 34px;
              }
            }
          }
          .accent-card--without-image {
            .accent-card__title {
              font-size: 24px;
              line-height: 34px;
              @include tablet {
                font-size: 26px;
                line-height: 34px;
              }
              @include big-tablet {
                font-size: 32px;
                line-height: 42px;
              }
            }
          }
        }
      }

      @include tablet {
        grid-column-gap: $spacing-3;
        border-radius: 48px;

        &--1,
        &--2,
        &--4 {
          grid-auto-columns: 1fr;
          max-width: 828px;
          padding: $spacing-6;
          @include big-tablet {
            padding: $spacing-8;
          }
          @include desktop {
            padding: $spacing-10;
          }
        }

        &--2,
        &--4 {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      &--client {
      }
      &--noscript {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        z-index: 3;
      }
    }

    &__card-wrapper {
      position: relative;
      border-radius: 32px;
      overflow: hidden;
    }

    &__placeholder {
      position: absolute;
      width: 100%;
      height: 100%;
      left: 0;
      top: 0;
      z-index: 3;
    }

    &--home,
    &--above-comments,
    &--below-comments,
    &--after-first-paragraph,
    &--post-dynamic {
      :deep() {
        .accent-card--big {
          padding-bottom: 0px;
        }
      }
    }

    &--above-comments,
    &--below-comments,
    &--after-first-paragraph,
    &--post-dynamic {
      .accent-cards__wrapper {
        padding: $spacing-4 $spacing-4 $spacing-6;
        margin: 0 -16px;
        border-radius: 48px;
        @include tablet {
          margin: 0;
          padding: $spacing-6;
        }
      }

      :deep() {
        .accent-card--small {
          @include tablet {
            min-height: 348px;
            max-height: 348px;
          }
          @include big-tablet {
            min-height: 440px;
            max-height: 440px;
          }
          .accent-card__title {
            @include tablet {
              font-size: 20px;
              line-height: 28px;
            }

            @include big-tablet {
              font-size: 22px;
              line-height: 32px;
            }
          }
        }
        .accent-card--without-image {
          .accent-card__title {
            @include tablet {
              font-size: 22px;
              line-height: 32px;
            }
            @include big-tablet {
              font-size: 26px;
              line-height: 34px;
            }
          }
        }
      }
    }

    &--sidebar {
      .accent-cards__wrapper {
        padding: 0;
      }

      :deep() {
        .accent-card {
          max-height: 400px;
          padding: 8px;
        }
        .accent-card__title {
          font-size: 20px;
          line-height: 28px;
        }
        .accent-card--without-image {
          .accent-card__title {
            font-size: 22px;
            line-height: 32px;
          }
        }
      }
    }
  }
</style>
